import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Monitor } from 'lucide-react';
import { getCanonicalUrl } from '../utils/config';

const TermsPage = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service - URL Monitor Pro | Website Monitoring Terms</title>
        <meta name="description" content="Read the Terms of Service for URL Monitor Pro website monitoring service. Learn about our service terms, user responsibilities, and legal agreements." />
        <meta name="keywords" content="terms of service, legal, website monitoring terms, user agreement, service terms" />
        <link rel="canonical" href={getCanonicalUrl('/terms')} />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center">
                <Monitor className="w-8 h-8 text-blue-600 mr-3" />
                <span className="text-xl font-bold text-gray-900">URL Monitor Pro</span>
              </Link>
              <div className="hidden md:flex items-center space-x-8">
                <a href="/#features" className="text-gray-600 hover:text-gray-900 transition-colors">Features</a>
                <Link to="/pricing" className="text-gray-600 hover:text-gray-900 transition-colors">Pricing</Link>
                <Link to="/about" className="text-gray-600 hover:text-gray-900 transition-colors">About</Link>
                <Link to="/contact" className="text-gray-600 hover:text-gray-900 transition-colors">Contact</Link>
                <Link to="/login" className="text-gray-600 hover:text-gray-900 transition-colors">Login</Link>
                <Link to="/register" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                  Start Free
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Header */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
            <p className="text-gray-600">Last updated: January 25, 2025</p>
          </div>
        </section>

        {/* Content */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none text-left">
              <style>
                {`
                  .prose h2 {
                    margin-top: 2rem;
                    margin-bottom: 1rem;
                  }
                  .prose p {
                    margin-bottom: 1rem;
                    text-align: left;
                  }
                  .prose ul, .prose ol {
                    margin-top: 0.5rem;
                    margin-bottom: 1.5rem;
                    text-align: left;
                  }
                `}
              </style>

              <h2>1. Company Information</h2>
              <p>
                URL Monitor Pro is a product operated by Publive Mediatech Private Limited, a company registered in India.
              </p>
              <ul>
                <li>Company Name: Publive Mediatech Private Limited</li>
                <li>Website: <a href="https://www.thepublive.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700">www.thepublive.com</a></li>
                <li>Location: Gurgaon, Haryana, India</li>
                <li>Product: URL Monitor Pro (Website Uptime Monitoring Service)</li>
              </ul>

              <h2>2. Acceptance of Terms</h2>
              <p>
                By accessing and using URL Monitor Pro ("Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>

              <h2>3. Description of Service</h2>
              <p>
                URL Monitor Pro provides website uptime monitoring services including but not limited to:
              </p>
              <p>
                Service features vary by subscription plan (Free, Pro, Business, Enterprise).
              </p>
              <ul>
                <li>24/7 website availability monitoring</li>
                <li>Performance tracking and analytics</li>
                <li>Instant downtime alerts via email, SMS, and other channels</li>
                <li>Incident reporting and historical data</li>
                <li>Dashboard and reporting tools</li>
              </ul>

              <h2>4. User Accounts</h2>
              <p>
                To use our Service, you must create an account. You are responsible for:
              </p>
              <ul>
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Providing accurate and complete information when creating your account</li>
                <li>Updating your information to keep it accurate and complete</li>
              </ul>

              <h2>5. Acceptable Use</h2>
              <p>You agree not to use the Service to:</p>
              <ul>
                <li>Monitor websites you do not own or have explicit permission to monitor</li>
                <li>Engage in any activity that interferes with or disrupts the Service</li>
                <li>Attempt to gain unauthorized access to any systems or networks</li>
                <li>Use the Service for any illegal or unauthorized purpose</li>
                <li>Transmit any harmful, threatening, abusive, or offensive content</li>
              </ul>

              <h2>6. Service Availability</h2>
              <p>
                While we strive for high availability, we do not guarantee that the Service will be available 100% of the time. The Service may be subject to:
              </p>
              <ul>
                <li>Scheduled maintenance</li>
                <li>Emergency maintenance</li>
                <li>Third-party service outages</li>
                <li>Force majeure events</li>
              </ul>

              <h2>7. Billing and Payment</h2>
              <p>
                For paid plans:
              </p>
              <ul>
                <li>Fees are charged in advance on a monthly or annual basis</li>
                <li>No refunds will be provided for partial months of service</li>
                <li>We reserve the right to change our pricing with 30 days notice</li>
                <li>Failure to pay may result in service suspension or termination</li>
                <li>You are responsible for all taxes associated with your use of the Service</li>
              </ul>

              <h2>8. Data and Privacy</h2>
              <p>
                Your privacy is important to us. Please review our Privacy Policy, which also governs your use of the Service, to understand our practices.
              </p>

              <h2>9. Intellectual Property</h2>
              <p>
                The Service and its original content, features, and functionality are and will remain the exclusive property of URL Monitor Pro and its licensors. The Service is protected by copyright, trademark, and other laws.
              </p>

              <h2>10. Limitation of Liability</h2>
              <p>
                In no event shall URL Monitor Pro, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the Service.
              </p>

              <h2>11. Disclaimer</h2>
              <p>
                The information on this Service is provided on an "as is" basis. To the fullest extent permitted by law, URL Monitor Pro excludes all representations, warranties, conditions, and terms whether express or implied.
              </p>

              <h2>12. Termination</h2>
              <p>
                We may terminate or suspend your account and bar access to the Service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited to a breach of the Terms.
              </p>

              <h2>13. Changes to Terms</h2>
              <p>
                We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days notice prior to any new terms taking effect.
              </p>

              <h2>14. Governing Law</h2>
              <p>
                These Terms shall be interpreted and governed by the laws of India, without regard to its conflict of law provisions. Any disputes arising from these terms shall be subject to the jurisdiction of courts in Gurgaon, Haryana, India.
              </p>

              <h2>15. Contact Information</h2>
              <p>
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <p>
                <strong>Publive Mediatech Private Limited</strong><br/>
                Email: <a href="mailto:support@urlmonitor.pro" className="text-blue-600 hover:text-blue-700">support@urlmonitor.pro</a><br/>
                Website: <a href="https://www.thepublive.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700">www.thepublive.com</a><br/>
                Location: Gurgaon, Haryana, India
              </p>

            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <Link to="/" className="flex items-center justify-center mb-4">
                <Monitor className="w-8 h-8 text-blue-400 mr-3" />
                <span className="text-xl font-bold">URL Monitor Pro</span>
              </Link>
              <p className="text-gray-400">
                © 2025 URL Monitor Pro. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default TermsPage;